import { createSlice } from '@reduxjs/toolkit';
import type { Action, PayloadAction } from '@reduxjs/toolkit';
import {
  ClientStatus,
  ActionStatus,
  ApiExError,
  apiReponseErrorIncludes,
  calcClientDerivatives,
  isDefined,
  isNullOrUndefined,
} from '@shared';
import { initialState } from './initialState';
import { fetchClientsForAdvisorByAdvisorIdResponse } from './clientsModel';

const clientSlice = createSlice({
  name: 'client',
  initialState: initialState.client,
  reducers: {
    resetClientStatus(state) {
      state.status = ActionStatus.idle;
    },
    previewClientUpdates(state, { payload }: PayloadAction<Client>) {
      return {
        ...state,
        data: calcClientDerivatives(payload, state.clientPartner),
      };
    },
    previewPhaseCosts(state, { payload }: PayloadAction<Client>) {
      const calculatedDerivatives = calcClientDerivatives(payload, state.clientPartner);

      if (calculatedDerivatives) {
        state.phaseCosts = calculatedDerivatives.phaseCosts;
        state.supportProviderSet = calculatedDerivatives.supportProviderSet;
      }
    },
    fetchClientByClientIdRequest(state, action: PayloadAction<string>) {
      if (action.payload === state.data?.clientId) {
        return state;
      }
      state.isLoading = true;
    },
    fetchClientByClientIdResponse(state, _action: PayloadAction<ClientContainer>) {
      state.isLoading = false;
      // the catch-all reducer will get it below..
    },
    fetchClientByClientIdFailure(_state, _action: PayloadAction<ApiExError>) {
      if (apiReponseErrorIncludes(_action.payload, 'Requested client not found.')) {
        return { ..._state, errorMessage: 'Requested client not found.' };
      }
      return { ..._state, errorMessage: _action.payload.message };
    },
    fetchClientDeletionEligibilityByClientIdRequest(state, _action: PayloadAction<string>) {
      state.status = ActionStatus.loading;
    },
    fetchClientDeletionEligibilityByClientIdResponse(
      state,
      action: PayloadAction<{ deletionStatus: ClientDeletionStatus }>,
    ) {
      state.status = ActionStatus.idle;
      state.deletionStatus = action.payload.deletionStatus;
    },
    fetchClientDeletionEligibilityByClientIdFailure(_state, _action: PayloadAction<ApiExError>) {
      return { ..._state, errorMessage: _action.payload.message };
    },
    putClientByClientIdRequest(state, action: PayloadAction<Client>) {
      if (action.payload.clientStatus === ClientStatus.archived) {
        state.status = ActionStatus.archiving;
      }
    },
    putClientByClientIdResponse(state, action: PayloadAction<ClientContainer>) {
      if (action.payload.client?.clientStatus === ClientStatus.archived) {
        state.status = ActionStatus.archived;
      }
    },
    putClientByClientIdFailure(_state, _action: PayloadAction<ApiExError>) {
      return initialState.client;
    },

    putFundingSourcesForClientByClientIdRequest(
      state,
      { payload: { clientId, fundingSources } }: PayloadAction<PutFundingSourcesBodyProps>,
    ) {
      if (isDefined(state.data) && state.data.clientId === clientId) {
        state.data.fundingSources = fundingSources;
        state.data = calcClientDerivatives(state.data, state.clientPartner);
      }
      state.isLoading = true;
    },
    putFundingSourcesForClientByClientIdResponse(state, _action: PayloadAction<ClientContainer>) {
      state.isLoading = false;
    },
    putFundingSourcesForClientByClientIdFailure(state, _action: PayloadAction<ApiExError>) {
      state.isLoading = false;
      return initialState.client;
    },

    putMultipleFundingSourceForClientByClientIdRequest(
      state,
      {
        payload: { clientId, fundingSourceId, fundingSourceType, fundingSourceData },
      }: PayloadAction<PutMultipleFundingSourceByIdBodyProps>,
    ) {
      state.isLoading = true;

      // Extract insuranceQuoteId from fundingSourceData for add/edit operations
      // Only PolicyFundingSource has insuranceQuoteId, so we check if it exists
      const insuranceQuoteId =
        fundingSourceData && 'insuranceQuoteId' in fundingSourceData
          ? fundingSourceData.insuranceQuoteId || null
          : null;

      const operationType =
        fundingSourceData === null ? 'delete' : fundingSourceId ? 'edit' : 'add';

      state.activeFundingSourceOperation = {
        fundingSourceId, // For deletes, this is the key we track
        insuranceQuoteId, // For adds, this is the key we track
        operationType: operationType as 'add' | 'edit' | 'delete',
      };
    },
    putMultipleFundingSourceForClientByClientIdResponse(
      state,
      _action: PayloadAction<PutMultipleFundingSourceResponse>,
    ) {
      state.isLoading = false;
      state.activeFundingSourceOperation = null;
    },

    putMultipleFundingSourceForClientByClientIdFailure(state, _action: PayloadAction<ApiExError>) {
      state.isLoading = false;
      state.activeFundingSourceOperation = null;
      return initialState.client;
    },

    putMirroredFundingSourceForClientByClientIdRequest(
      state,
      _action: PayloadAction<PutMirroredFundingSourceBodyProps>,
    ) {
      state.isLoading = true;
    },
    putMirroredFundingSourceForClientByClientIdResponse(
      state,
      _action: PayloadAction<ClientContainer>,
    ) {
      state.isLoading = false;
    },
    putMirroredFundingSourceForClientByClientIdFailure(state, _action: PayloadAction<ApiExError>) {
      state.isLoading = false;
    },

    putBatchSelfFundingExistingAssetsValueForClientByClientIdRequest(
      state,
      _action: PayloadAction<PutBatchSelfFundingExistingAssetsValueBodyProps>,
    ) {
      state.isLoading = true;
    },
    putBatchSelfFundingExistingAssetsValueForClientByClientIdResponse(
      state,
      _action: PayloadAction<ClientContainer>,
    ) {
      state.isLoading = false;
    },
    putBatchSelfFundingExistingAssetsValueForClientByClientIdFailure(
      state,
      _action: PayloadAction<ApiExError>,
    ) {
      state.isLoading = false;
      return initialState.client;
    },

    putSupportProviderSetForClientByClientIdRequest(
      state,
      { payload: { supportProviderSet } }: PayloadAction<PutSupportProviderSetBodyProps>,
    ) {
      return {
        ...state!,
        isPartialLoading: true,
        supportProviderSet,
      };
    },
    putSupportProviderSetForClientByClientIdResponse(
      state,
      _action: PayloadAction<ClientContainer>,
    ) {
      state.isPartialLoading = false;
      state.phaseCosts = [];
      state.supportProviderSet = null;
    },
    putSupportProviderSetForClientByClientIdFailure(state, _action: PayloadAction<ApiExError>) {
      state.isPartialLoading = false;
      state.phaseCosts = [];
      state.supportProviderSet = null;
    },

    postSurveysBySurveyIdRequest(_state, _action: PayloadAction<Survey[]>) {},
    postSurveysBySurveyIdResponse(_state, _action: PayloadAction<ClientContainer>) {},
    postSurveysBySurveyIdFailure(_state, _action: PayloadAction<ApiExError>) {},

    putCareEnvironmentSelectionsForClientByClientIdRequest(
      state,
      _action: PayloadAction<PutCareEnvironmentSelectionsBodyProps>,
    ) {
      state.isLoading = true;
      state.clientUpdates.careEnvironmentSelections = 'loading';
    },
    putCareEnvironmentSelectionsForClientByClientIdResponse(
      state,
      _action: PayloadAction<ClientContainer>,
    ) {
      state.isLoading = false;
      state.clientUpdates.careEnvironmentSelections = 'complete';
    },
    putCareEnvironmentSelectionsForClientByClientIdFailure(
      state,
      _action: PayloadAction<ApiExError>,
    ) {
      state.isLoading = false;
      state.clientUpdates.careEnvironmentSelections = 'error';
    },

    putClientCareEnvironmentCostsForClientByClientIdRequest(
      state,
      _action: PayloadAction<PutClientCareEnvironmentCostsBodyProps>,
    ) {
      state.isLoading = true;
      state.clientUpdates.careEnvironmentCosts = 'loading';
    },
    putClientCareEnvironmentCostsForClientByClientIdResponse(
      state,
      _action: PayloadAction<ClientContainer>,
    ) {
      state.isLoading = false;
      state.clientUpdates.careEnvironmentCosts = 'complete';
    },
    putClientCareEnvironmentCostsForClientByClientIdFailure(
      state,
      _action: PayloadAction<ApiExError>,
    ) {
      state.isLoading = false;
      state.clientUpdates.careEnvironmentCosts = 'error';
    },

    putClientCarePhaseDurationSelectionForClientByClientIdRequest(
      state,
      _action: PayloadAction<PutCarePhaseDurationSelectionBodyProps>,
    ) {
      state.clientUpdates.phaseDurations = 'loading';
    },
    putClientCarePhaseDurationSelectionForClientByClientIdResponse(
      state,
      _action: PayloadAction<ClientContainer>,
    ) {
      state.clientUpdates.phaseDurations = 'complete';
    },
    putClientCarePhaseDurationSelectionForClientByClientIdFailure(
      state,
      _action: PayloadAction<ApiExError>,
    ) {
      state.clientUpdates.phaseDurations = 'error';
    },
    putClientOnboardingSlideProgressByClientIdRequest(
      _state,
      _action: PayloadAction<ClientOnboardingSlideProgress>,
    ) {},
    putClientOnboardingSlideProgressByClientIdResponse(
      _state,
      _action: PayloadAction<ClientContainer>,
    ) {},
    putClientOnboardingSlideProgressByClientIdFailure(
      _state,
      _action: PayloadAction<ApiExError>,
    ) {},
    deleteClientByClientIdRequest(state, _action: PayloadAction<DeleteClientProps>) {
      state.status = ActionStatus.deleting;
    },
    deleteClientByClientIdResponse(state, _action: PayloadAction<Advisor>) {
      state.status = ActionStatus.deleted;
    },
    deleteClientByClientIdFailure(state, _action: PayloadAction<ApiExError>) {
      state.status = ActionStatus.deleteFailed;
      return initialState.client;
    },
    putClientPartnerLinkForClientByClientIdRequest(
      _state,
      action: PayloadAction<PutClientPartnerLinkBodyProps>,
    ) {},
    putClientPartnerLinkForClientByClientIdResponse(
      state,
      { payload: { client, clientPartner } }: PayloadAction<ClientContainer>,
    ) {
      if (client) {
        const newClientPartner = clientPartner ? calcClientDerivatives(clientPartner, null) : null;
        state.data = calcClientDerivatives(client, newClientPartner);
        state.clientPartner = newClientPartner;
      }
      state.isLoading = false;
    },
    putClientPartnerLinkForClientByClientIdFailure(_state, action: PayloadAction<ApiExError>) {
      return initialState.client;
    },
    putClientCustomInferencesForClientByClientIdRequest(
      state,
      _action: PayloadAction<PutClientCustomInferencesBodyProps>,
    ) {
      state.clientUpdates.ltcAtAge = 'loading';
      state.isLoading = true;
    },
    putClientCustomInferencesForClientByClientIdResponse(
      state,
      _action: PayloadAction<ClientContainer>,
    ) {
      state.clientUpdates.ltcAtAge = 'complete';
      state.isLoading = false;
    },
    putClientCustomInferencesForClientByClientIdFailure(state, _action: PayloadAction<ApiExError>) {
      state.clientUpdates.ltcAtAge = 'error';
      state.isLoading = false;
    },
    resetClientUpdatesStatus(state, action: PayloadAction<ClientUpdateKeys>) {
      state.clientUpdates[action.payload] = null;
      state.errorMessage = null;
    },
    postAdvisorMeetingRequestEmailRequest(
      state,
      _action: PayloadAction<AdvisorMeetingRequestEmailProps>,
    ) {
      state.clientUpdates.advisorMeetingRequest = 'loading';
    },
    postAdvisorMeetingRequestEmailResponse(state, _action: PayloadAction<string>) {
      state.clientUpdates.advisorMeetingRequest = 'complete';
    },
    postAdvisorMeetingRequestEmailFailure(state, _action: PayloadAction<ApiExError>) {
      state.clientUpdates.advisorMeetingRequest = 'error';
    },
    putClientTagsByClientIdRequest(_state, _action: PayloadAction<PutClientTagsBodyProps>) {},
    putClientTagsByClientIdResponse(_state, _action: PayloadAction<ClientContainer>) {},
    putClientTagsByClientIdFailure(_state, _action: PayloadAction<ApiExError>) {},
    putPartnerLinkRequestEmailRequest(
      state,
      _action: PayloadAction<PutPartnerLinkRequestEmailProps>,
    ) {
      state.clientUpdates.partnerLinkRequest = 'loading';
    },
    putPartnerLinkRequestEmailResponse(state, _action: PayloadAction<ClientContainer>) {
      state.clientUpdates.partnerLinkRequest = 'complete';
    },
    putPartnerLinkRequestEmailFailure(state, action: PayloadAction<ApiExError>) {
      state.clientUpdates.partnerLinkRequest = 'error';

      if (apiReponseErrorIncludes(action.payload, 'not found')) {
        state.errorMessage =
          'The account with the provided email address was not found for this advisor.';
      }
      if (apiReponseErrorIncludes(action.payload, 'multiple records')) {
        state.errorMessage =
          'There are multiple clients with that email address for this advisor. Please contact your advisor to    link the accounts for you.';
      }
      if (apiReponseErrorIncludes(action.payload, 'already linked')) {
        state.errorMessage =
          'The account with the provided email address is already linked to another client.';
      }
      if (apiReponseErrorIncludes(action.payload, 'no inference set')) {
        state.errorMessage =
          'The account with the provided email address has not submitted an intake form.';
      }
    },
    putClientCalculationSettingsByClientIdRequest(
      state,
      _action: PayloadAction<PutClientCalculationSettingsBodyProps>,
    ) {
      state.clientUpdates.healthCareInflationRate = 'loading';
    },
    putClientCalculationSettingsByClientIdResponse(state, _action: PayloadAction<ClientContainer>) {
      state.clientUpdates.healthCareInflationRate = 'complete';
    },
    putClientCalculationSettingsByClientIdFailure(state, _action: PayloadAction<ApiExError>) {
      state.clientUpdates.healthCareInflationRate = 'error';
    },
    putClientCalculationsByClientIdRequest(
      _state,
      _action: PayloadAction<PutClientCalculationsBodyProps>,
    ) {},
    putClientCalculationsByClientIdResponse(_state, _action: PayloadAction<ClientContainer>) {},
    putClientCalculationsByClientIdFailure(_state, _action: PayloadAction<ApiExError>) {},
    putClientRelationshipStageByClientIdRequest(
      _state,
      _action: PayloadAction<PutClientRelationshipStageBodyProps>,
    ) {},
    putClientRelationshipStageByClientIdResponse(
      _state,
      _action: PayloadAction<ClientContainer>,
    ) {},
    putClientRelationshipStageByClientIdFailure(_state, _action: PayloadAction<ApiExError>) {},
    putClientReferralSourceByClientIdRequest(
      _state,
      _action: PayloadAction<PutClientReferralSourceBodyProps>,
    ) {},
    putClientReferralSourceByClientIdResponse(_state, _action: PayloadAction<ClientContainer>) {},
    putClientReferralSourceByClientIdFailure(_state, _action: PayloadAction<ApiExError>) {},
    postEngagementTimeTrackingMilestoneRequest(
      state,
      action: PayloadAction<{ userType: string; advisorId: string; clientId: string }>,
    ) {},
  },

  extraReducers(builder) {
    builder.addCase(
      fetchClientsForAdvisorByAdvisorIdResponse,
      (state, { payload: clients }: PayloadAction<Client[]>) => {
        if (!state.data) {
          return;
        }
        const refreshedClient = clients.find(c => c.clientId === state.data!.clientId);
        if (
          refreshedClient &&
          (refreshedClient.clientFirstName !== state.data.clientFirstName ||
            refreshedClient.clientLastName !== state.data.clientLastName)
        ) {
          state.data = null;
        }
      },
    );

    builder.addMatcher(
      isClientResponseAction,
      (state, { payload: { client, clientPartner } }: PayloadAction<ClientContainer>) => {
        if (client) {
          // When a mutation targets the partner (e.g. the second dispatch in a
          // Joint Life policy add), the server returns { client: partner,
          // clientPartner: primary }. Route that response to state.clientPartner
          // so we don't overwrite state.data with the partner record, which
          // would break ClientRoute's clientId check and force a page remount.
          const isPartnerResponse =
            state.data != null &&
            state.data.clientId !== client.clientId &&
            state.clientPartner?.clientId === client.clientId;

          if (isPartnerResponse) {
            state.clientPartner = calcClientDerivatives(client, null);
          } else {
            const isDashboardClient = isNullOrUndefined(client.inferenceSet);
            const newClientPartner = clientPartner
              ? calcClientDerivatives(clientPartner, null)
              : null;
            state.data = isDashboardClient
              ? client
              : calcClientDerivatives(client, newClientPartner);
            state.clientPartner = newClientPartner;
          }
        }

        if (state.isLoading === true) {
          state.isLoading = false;
        }
      },
    );
  },
});

export const {
  resetClientStatus,
  previewClientUpdates,
  previewPhaseCosts,
  fetchClientByClientIdRequest,
  fetchClientByClientIdResponse,
  fetchClientByClientIdFailure,
  fetchClientDeletionEligibilityByClientIdRequest,
  fetchClientDeletionEligibilityByClientIdResponse,
  fetchClientDeletionEligibilityByClientIdFailure,
  putClientByClientIdRequest,
  putClientByClientIdResponse,
  putClientByClientIdFailure,
  putFundingSourcesForClientByClientIdRequest,
  putFundingSourcesForClientByClientIdResponse,
  putFundingSourcesForClientByClientIdFailure,
  putMultipleFundingSourceForClientByClientIdRequest,
  putMultipleFundingSourceForClientByClientIdResponse,
  putMultipleFundingSourceForClientByClientIdFailure,
  putMirroredFundingSourceForClientByClientIdRequest,
  putMirroredFundingSourceForClientByClientIdResponse,
  putMirroredFundingSourceForClientByClientIdFailure,
  putBatchSelfFundingExistingAssetsValueForClientByClientIdRequest,
  putBatchSelfFundingExistingAssetsValueForClientByClientIdResponse,
  putBatchSelfFundingExistingAssetsValueForClientByClientIdFailure,
  putSupportProviderSetForClientByClientIdRequest,
  putSupportProviderSetForClientByClientIdResponse,
  putSupportProviderSetForClientByClientIdFailure,
  postSurveysBySurveyIdRequest,
  postSurveysBySurveyIdFailure,
  postSurveysBySurveyIdResponse,
  putCareEnvironmentSelectionsForClientByClientIdRequest,
  putCareEnvironmentSelectionsForClientByClientIdResponse,
  putCareEnvironmentSelectionsForClientByClientIdFailure,
  putClientCareEnvironmentCostsForClientByClientIdRequest,
  putClientCareEnvironmentCostsForClientByClientIdResponse,
  putClientCareEnvironmentCostsForClientByClientIdFailure,
  putClientCarePhaseDurationSelectionForClientByClientIdRequest,
  putClientCarePhaseDurationSelectionForClientByClientIdResponse,
  putClientCarePhaseDurationSelectionForClientByClientIdFailure,
  putClientOnboardingSlideProgressByClientIdRequest,
  putClientOnboardingSlideProgressByClientIdResponse,
  putClientOnboardingSlideProgressByClientIdFailure,
  deleteClientByClientIdRequest,
  deleteClientByClientIdResponse,
  deleteClientByClientIdFailure,
  putClientPartnerLinkForClientByClientIdRequest,
  putClientPartnerLinkForClientByClientIdResponse,
  putClientPartnerLinkForClientByClientIdFailure,
  putClientCustomInferencesForClientByClientIdRequest,
  putClientCustomInferencesForClientByClientIdResponse,
  putClientCustomInferencesForClientByClientIdFailure,
  resetClientUpdatesStatus,
  postAdvisorMeetingRequestEmailRequest,
  postAdvisorMeetingRequestEmailResponse,
  postAdvisorMeetingRequestEmailFailure,
  putClientTagsByClientIdRequest,
  putClientTagsByClientIdResponse,
  putClientTagsByClientIdFailure,
  putPartnerLinkRequestEmailRequest,
  putPartnerLinkRequestEmailResponse,
  putPartnerLinkRequestEmailFailure,
  putClientCalculationSettingsByClientIdRequest,
  putClientCalculationSettingsByClientIdResponse,
  putClientCalculationSettingsByClientIdFailure,
  putClientCalculationsByClientIdRequest,
  putClientCalculationsByClientIdResponse,
  putClientCalculationsByClientIdFailure,
  putClientRelationshipStageByClientIdRequest,
  putClientRelationshipStageByClientIdResponse,
  putClientRelationshipStageByClientIdFailure,
  putClientReferralSourceByClientIdRequest,
  putClientReferralSourceByClientIdResponse,
  putClientReferralSourceByClientIdFailure,
  postEngagementTimeTrackingMilestoneRequest,
} = clientSlice.actions;

export const clientReducer = clientSlice.reducer;

export function isClientResponseAction(
  action: Action<any>,
): action is PayloadAction<ClientContainer> {
  if (!('payload' in action)) {
    return false;
  }
  const { payload } = action as PayloadAction<ClientContainer>;
  return (
    !action.type.toLocaleLowerCase().includes('advisorhierarchy') &&
    !action.type.toLocaleLowerCase().includes('clientpartner') &&
    !action.type.toLocaleLowerCase().includes('advisoradminportal') &&
    !action.type.toLocaleLowerCase().includes('partnerlinkrequest') &&
    action.type.endsWith('Response') &&
    isDefined(payload) &&
    'client' in payload &&
    isDefined(payload.client)
  );
}
